import { useEffect, useState } from "react";
import transferIconActive from "@src/assets/svg/transfer-arrow-icon-active.svg";
import transferIconInactive from "@src/assets/svg/transfer-arrow-icon.svg";
import { useDispatch } from "react-redux";
import { updateShowOverwriteStructureWarningModal } from "@src/reducers/your-reducer";
import { getInitialStructureNodeset } from "@src/cloud-connect/dependency-injection-container/get-initial-structure-nodeset";
import { getMachineStructureNodeset } from "@src/cloud-connect/dependency-injection-container/get-machine-structure-nodeset";
import { transferInitialListToEmptyOurNodeMachineStructure } from "@src/components/organisms/tree-structure/tree-components/tree-our-node/use-cases/transfer-initial-list-to-empty-our-node-machine-structure";
import { useI18N } from "@src/language/language-context";

export interface TransferButtonNewStructureObj {
  handleButtonVariants: () => string[];
  transfer: () => void;
  handleTransferButtonIconColor: () => string;
  transferText: string;
  tooltip: string;
  areNoCheckboxesSelected: () => boolean;
  initialStructureSelectedNodeCount: number;
}

export const useTransferButtonNewStructureHook = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const initialStructureNodeset = getInitialStructureNodeset();

  const [
    initialStructureSelectedNodeCount,
    setInitialStructureSelectedNodeCount,
  ] = useState(initialStructureNodeset.getSelectedNodeCount());

  const checkIfMachineStructureIsEmpty = () => {
    getMachineStructureNodeset().getRootNode().children.length !== 0
      ? dispatch(updateShowOverwriteStructureWarningModal(true))
      : transferInitialListToEmptyOurNodeMachineStructure();
  };

  const transfer = () => {
    checkIfMachineStructureIsEmpty();
  };

  const texts = {
    tooltip: i18n("modules.transferButton.tooltip"),
    transferTextCountPlaceholder: i18n(
      "modules.transferButton.transferTextCountPlaceholder",
    ),
    transferTextNoItems: i18n("modules.transferButton.transferTextNoItems"),
    transferTextSingularTemplate: i18n(
      "modules.transferButton.transferTextSingularTemplate",
    ),
    transferTextPluralTemplate: i18n(
      "modules.transferButton.transferTextPluralTemplate",
    ),
  };

  const transferText = (count: number) => {
    const getTransferTextTemplate = (count: number) => {
      if (count === 1) {
        return texts.transferTextSingularTemplate;
      } else if (count > 1) {
        return texts.transferTextPluralTemplate;
      } else {
        return texts.transferTextNoItems;
      }
    };

    const buildTransferText = (
      template: string,
      placeholder: string,
      count: number,
    ) => {
      return template.replace(placeholder, count.toString());
    };

    return buildTransferText(
      getTransferTextTemplate(count),
      texts.transferTextCountPlaceholder,
      count,
    );
  };

  const areNoCheckboxesSelected = () => {
    return initialStructureSelectedNodeCount === 0 ? true : false;
  };

  const handleTransferButtonIconColor = () => {
    return areNoCheckboxesSelected()
      ? transferIconInactive
      : transferIconActive;
  };

  const handleButtonVariants = () => {
    const variantsArray = ["reverse", "no-outline"];
    if (initialStructureSelectedNodeCount !== 0) {
      variantsArray.push("color-blue");
    }
    return variantsArray;
  };

  useEffect(() => {
    const updateCount = () => {
      setInitialStructureSelectedNodeCount(
        initialStructureNodeset.getSelectedNodeCount(),
      );
    };

    initialStructureNodeset.addObserver(updateCount);
    return () => {
      initialStructureNodeset.removeObserver(updateCount);
    };
  }, []);

  return {
    handleButtonVariants: handleButtonVariants,
    transfer: transfer,
    handleTransferButtonIconColor: handleTransferButtonIconColor,
    transferText: transferText(initialStructureSelectedNodeCount),
    tooltip: texts.tooltip,
    areNoCheckboxesSelected: areNoCheckboxesSelected,
    initialStructureSelectedNodeCount: initialStructureSelectedNodeCount,
  };
};
